'use client';

import React, {
  Context,
  createContext,
  FC,
  ReactNode,
  useCallback, useEffect,
  useMemo,
  useState
} from 'react';
import { useRouter } from 'next/navigation';
import { useMedia } from 'react-use';
import { Controller, useForm } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import * as z from 'zod';

import { Button } from 'components/ui/button';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';

import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useToast } from 'hooks/useToast';
import { Sheet, SheetContent } from 'components/ui/sheet';
import { useAddNewDealMutation, useDeleteDealMutation, useEditDealMutation } from 'generated/hooks';
import { DealsQuery } from 'generated/operations';
import { LoaderCircle } from 'lucide-react';
import { LogoutIcon } from 'components/icons/v2';
import { Scalars } from 'generated/types';


type Deal = NonNullable<DealsQuery['deals']>[number];

interface DealsContextProps {
  openDealModal: (open: boolean, deal: Deal | undefined) => void;
  openDealDeleteModal: (open: boolean, deal: Deal) => void;
}

const defaultValue: DealsContextProps = {
  openDealModal: () => void 0,
  openDealDeleteModal: () => void 0
};

export const DealsContext: Context<DealsContextProps> = createContext(defaultValue);

interface DealForm {
  title: string;
  request: string;
  fee: number;
  detailsUrl?: string;
}

const needHelpSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'This field is required')
    .max(25, 'Title is too long'),
  request: z
    .string()
    .trim()
    .min(1, 'This field is required')
    .max(222, 'Your deal description is too long'),
  fee: z
    .number({ message: 'Fee is required' })
    .min(0, 'Fee must be a positive number'),
  detailsUrl: z
    .string()
    .trim()
    .optional()
});


type Tab = 'deal' | 'service'

const TabSwitcher = ({
  activeTab,
  onTabChange,
}: {
  activeTab: Tab
  onTabChange: (tab: Tab) => void
}) => {
  return (
    <div className="flex p-1 bg-gray-100 rounded-xl relative">
      <button
        onClick={() => onTabChange('deal')}
        className={`flex-1 relative z-10 py-2 text-sm font-medium transition-colors duration-200 ${activeTab === 'deal' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        tabIndex={-1}
      >
        Deal
        {activeTab === 'deal' && (
          <div className='absolute inset-0 bg-white rounded-lg shadow-sm transition-all duration-300 ease-out'
            style={{
              zIndex: -1
            }}
          />
        )}
      </button>
      <button
        onClick={() => onTabChange('service')}
        className={`flex-1 relative z-10 py-2 text-sm font-medium transition-colors duration-200 ${activeTab === 'service' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        tabIndex={-1}
      >
        Service
        {activeTab === 'service' && (
          <div
            className='absolute inset-0 bg-white rounded-lg shadow-sm transition-all duration-[600ms] ease-out'
            style={{
              zIndex: -1
            }}
          />
        )}
      </button>
    </div>
  )
}

export const DealsProvider: FC<{ children: ReactNode; }> = (props) => {
  const router = useRouter();
  const [sendAddDealState, sendAddDeal] = useAddNewDealMutation();
  const [sendEditDealState, sendEditDeal] = useEditDealMutation();
  const [sendDeleteDealState, sendDeleteDeal] = useDeleteDealMutation();
  const { handleErrors } = useFormErrorHandling();
  const { handleToast } = useToast();

  const [isOpenModal, setOpenModal] = useState<boolean>(false);
  const [isOpenDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
  // if deal is present - deal update form
  const [deal, setDeal] = useState<Deal | undefined>(undefined)
  const [activeTab, setActiveTab] = useState<Tab>('deal');
  useEffect(() => {
    setActiveTab(deal?.isService ? 'service' : 'deal');
  }, [deal?.isService]);

  const {
    setError,
    control,
    handleSubmit,
    reset,
  } = useForm<DealForm>({
    mode: 'onChange',
    reValidateMode: 'onSubmit',
    values: {
      title: deal?.title ?? '',
      request: deal?.message ?? '',
      fee: deal?.fee ?? undefined as unknown as number,
      detailsUrl: deal?.detailsUrl ?? ''
    },
    resolver: standardSchemaResolver(needHelpSchema)
  });

  const value: DealsContextProps = useMemo(() => ({
    openDealModal: (open: boolean, deal: Deal | undefined) => {
      setOpenModal(open);
      setDeal(deal);
    },
    openDealDeleteModal: (open: boolean, deal: Deal) => {
      setOpenDeleteModal(open);
      setDeal(deal);
    }
  }), []);

  useEffect(() => {
    reset();
  }, [isOpenModal, reset]);

  const onSubmit = useCallback(async (data: DealForm) => {
    try {
      let response;
      if (deal) {
        response = await sendEditDeal({
          dealId: deal.id,
          title: data.title,
          message: data.request,
          fee: data.fee,
          detailsUrl: data.detailsUrl || undefined,
          isService: activeTab == 'service'
        });
      } else {
        response = await sendAddDeal({
          title: data.title,
          message: data.request,
          fee: data.fee,
          detailsUrl: data.detailsUrl || undefined,
          isService: activeTab == 'service'
        });
      }

      if (response.error) {
        console.log(response.error);
        return handleErrors<DealForm>(response.error, setError);
      }

      setOpenModal(prevState => !prevState);
      router.refresh();
    } catch (error) {
      console.log(error);
    }
  }, [handleErrors, handleToast, router, sendAddDeal, setError]);

  const handleDelete = useCallback(async (dealId: Scalars['ID']) => {
    setDeleteError(undefined);
    const response = await sendDeleteDeal({ dealId: dealId });
    if (response.error) {
      setDeleteError(response?.error?.graphQLErrors[0]?.message ?? 'Error during delete, please try again later');
      return
    }

    setOpenDeleteModal(prevState => !prevState);
    router.refresh();
  }, [deal])

  const isMobile = useMedia('(max-width: 768px)', false);
  const DialogRoot = isMobile ? Sheet : Dialog;
  const Content = isMobile ? SheetContent : DialogContent;

  return (
    <DealsContext.Provider value={value}>
      {props.children}
      {isOpenModal && (
        <DialogRoot open onOpenChange={() => {
          setOpenModal(() => false);
        }}>
          <Content
            showClose={true}
            className='p-0 bg-white md:!max-w-full md:!w-full md:h-full'
            side='top'
          >
            <div className='pt-8 pb-6 px-6 flex flex-col gap-6 bg-gray-50 md:p-4'>
              <div className='flex flex-col gap-sm'>
                <p className='text-display-sm text-black font-bold'>
                  {deal ? 'Edit ' : 'New '}
                  {activeTab == 'deal' ? 'deal' : 'service'}
                </p>
                <p className='text-medium text-gray-400'>
                  {activeTab == 'deal' ?
                    'Turn your network into action — hiring, intros, referrals, and trusted help' :
                    'Turn your expertise into opportunities — offer services, share skills, and get paid by your circle.'
                  }
                </p>
              </div>
              <TabSwitcher activeTab={activeTab} onTabChange={setActiveTab} />
              <form className='flex flex-col gap-6' onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  control={control}
                  name='title'
                  render={({ field, formState: { errors } }) => (
                    <div className='flex flex-col gap-1'>
                      <Input
                        labelClassName='!text-gray-700'
                        label='Title'
                        placeholder='Enter title'
                        errorText={errors.title?.message as string}
                        required
                        {...field}
                      />
                      <span className='text-sm text-gray-400 text-right'>
                        {field.value?.length || 0}/25
                      </span>
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name='request'
                  render={({ field, formState: { errors } }) => (
                    <div className='flex flex-col gap-1'>
                      <Textarea
                        labelClassName='!text-gray-700'
                        label='Description'
                        placeholder='Enter description'
                        rows={6}
                        errorText={errors.request?.message as string || errors.root?.message}
                        required
                        {...field}
                      />
                      <span className='text-sm text-gray-400 text-right'>
                        {field.value?.length || 0}/222
                      </span>
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name='fee'
                  render={({ field, formState: { errors } }) => (
                    <Input
                      labelClassName='!text-gray-700'
                      label='Fee'
                      placeholder='Enter fee'
                      type='number'
                      errorText={errors.fee?.message as string}
                      required
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name='detailsUrl'
                  render={({ field, formState: { errors } }) => (
                    <Input
                      labelClassName='!text-gray-700'
                      label='Details URL'
                      placeholder='Enter URL (optional)'
                      errorText={errors.detailsUrl?.message as string}
                      {...field}
                    />
                  )}
                />
                <Button type='submit' variant='black'>
                  Share
                  {(sendAddDealState.fetching || sendEditDealState.fetching) &&
                    <LoaderCircle className='animate-spin' />}
                </Button>
              </form>
            </div>
          </Content>
        </DialogRoot>
      )}

      {isOpenDeleteModal && deal && (
        <DialogRoot open onOpenChange={() => {
          setOpenDeleteModal(() => false);
        }}>
          <Content
            showClose={true}
            className='p-0 sm-1/2:max-w-full sm-1/2:w-full bg-gray-50'
            side='top'
          >
            <div className='px-6 pb-6 pt-8 flex flex-col gap-8'>
              <div className='flex flex-col flex-1 gap-4 items-center'>
                <LogoutIcon height={48} width={48} className='text-error-600' />
                <p className='text-display-xs text-black font-semibold text-center'>
                  Are you sure you want <br />
                  to delete the deal?
                </p>
                {deleteError && <p className={'text-medium text-error-500'}>{deleteError}</p>}
              </div>
              <div className='flex gap-[6px]'>
                <Button
                  type='button'
                  variant='nakedGray'
                  size='medium'
                  className='flex-1'
                  onClick={() => setOpenDeleteModal(() => false)}
                >
                  Nevermind
                </Button>
                <Button
                  type='button'
                  variant='primaryBrand'
                  size='medium'
                  className='flex-1 bg-error-600 hover:bg-error-800 border-error-600 hover:border-error-800'
                  onClick={() => handleDelete(deal.id)}
                >
                  Confirm
                  {sendDeleteDealState.fetching && <LoaderCircle className='animate-spin' />}
                </Button>
              </div>
            </div>
          </Content>
        </DialogRoot>
      )}

    </DealsContext.Provider>
  );
};
